import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';

import {
  HorizontalSelector,
  HorizontalSelectorOption,
} from '../../../controls-and-inputs/selects/HorizontalSelector';

import styles from './Tabs.module.css';

export interface TabOption<T extends string | number>
  extends HorizontalSelectorOption<T> {
  content: ReactNode;
  // Allow click callbacks to be fired but do not allow the tab to appear "active"
  preventActiveState?: boolean;
}

export interface TabsProps<T extends string | number> {
  /** For searchParam active tab */
  searchParamId?: string;
  className?: string;
  dataHcName: string;
  dataHcEventSection?: string;
  tabs: (TabOption<T> | null)[];
  active?: T;
  theme?: Partial<{ Content: string; Tabs: string; TabsSection: string }>;
  children?: ReactNode;
  /** Allow temporarily hiding the tabs */
  hideTabs?: boolean;
  onChange?: (value: T) => void;
  persistState?: boolean;
  scrollTopOnChange?: boolean;
  triggerScrollTop?: string | null;
}

const SEARCH_PARAM_ACTIVE_TAB = 'tabs-activeTab';
export const Tabs = <T extends string | number>({
  dataHcName,
  dataHcEventSection,
  className,
  searchParamId,
  tabs,
  onChange,
  hideTabs,
  active,
  children,
  theme,
  persistState = true,
  scrollTopOnChange = false,
  triggerScrollTop,
}: TabsProps<T>) => {
  const searchParamActiveTab = searchParamId
    ? `${SEARCH_PARAM_ACTIVE_TAB}-${searchParamId}`
    : undefined;
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTabId, setActiveTabId] = useState<T | undefined>(
    (persistState === true && searchParamActiveTab
      ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        (searchParams.get(searchParamActiveTab) as T | undefined)
      : undefined) ||
      active ||
      tabs[0]?.value,
  );
  const contentRef = useRef<HTMLDivElement>(null);
  const activeTab = useMemo(() => {
    const a = tabs.find((t) => t?.value === activeTabId);
    return a;
  }, [tabs, activeTabId]);
  const handleSetActiveTabId = (newActiveTabId: T) => {
    const newActiveTab = tabs.find((t) => t?.value === newActiveTabId);
    // Scroll to top when tab is clicked
    if (scrollTopOnChange && contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (!newActiveTab?.preventActiveState) {
      setActiveTabId(newActiveTabId);
      if (persistState && searchParamActiveTab) {
        searchParams.set(searchParamActiveTab, newActiveTabId.toString());
        setSearchParams(searchParams);
      }
      onChange?.(newActiveTabId);
    }
  };
  useEffect(() => {
    if (active) {
      setActiveTabId(active);
    }
  }, [active]);

  // Trigger scroll to top when triggerScrollTop changes
  useEffect(() => {
    if (triggerScrollTop && contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [triggerScrollTop]);

  return (
    <>
      {!hideTabs && (
        <div
          data-hc-name={dataHcName}
          data-hc-event-section={dataHcEventSection}
          className={classNames(
            styles.TabsSection,
            className,
            theme?.TabsSection,
          )}
        >
          <HorizontalSelector
            className={classNames(styles.Tabs, theme?.Tabs)}
            dataHcName={`${dataHcName}-horz-selector`}
            options={tabs.filter((t) => !!t)}
            onSelect={handleSetActiveTabId}
            value={activeTabId}
            theme={{
              List: styles.List,
              ListItem: styles.ListItem,
              Hr: styles.Hr,
            }}
          />
          <div>{children}</div>
        </div>
      )}
      {/* This component must only render the .Content div if `activeTab.content` is truthy to avoid breaking layouts */}
      {activeTab?.content && (
        <div
          ref={contentRef}
          data-hc-name={`${dataHcName}-content`}
          className={theme?.Content}
        >
          {activeTab.content}
        </div>
      )}
    </>
  );
};
